import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Award } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";

const HubRow = ({ hub, current, onSaved }) => {
  const { L, lang } = useApp();
  const a = (en, fr) => (lang === "fr" ? fr : en);
  const [val, setVal] = useState("");
  const save = async (post) => {
    try {
      const { data } = await api.put("/admin/spotlight", { hub_id: hub.id, post });
      onSaved(hub.id, data.post); setVal("");
      toast.success(post ? a("Spotlight updated", "Vedette mise à jour") : a("Spotlight removed", "Vedette retirée"));
    } catch (e) {
      const c = e?.response?.data?.detail;
      toast.error(c === "wrong_hub" ? a("That post is in another city", "Cette publication est dans une autre ville") : a("Post not found — paste the post link", "Publication introuvable — collez le lien"));
    }
  };
  return (
    <div className="py-2 space-y-1.5" data-testid={`spotlight-row-${hub.id}`}>
      <div className="flex items-center justify-between gap-2 text-sm">
        <b>{L(hub)}</b>
        {current ? <span className="truncate text-[#1E4D3B]">{current.title}{!["available", "open"].includes(current.status) && <span data-testid={`spotlight-hidden-${hub.id}`} className="ml-2 rounded-full bg-[#FDE2D3] text-[#9A3412] px-2 py-0.5 text-xs font-bold">{a("Hidden (sold/expired)", "Masquée (vendue/expirée)")}</span>}</span> : <span className="text-[#6B5E55]">{a("None", "Aucune")}</span>}
      </div>
      <div className="flex gap-2">
        <input data-testid={`spotlight-input-${hub.id}`} value={val} onChange={(e) => setVal(e.target.value)} placeholder={a("Paste post link", "Collez le lien de la publication")}
          className="flex-1 min-w-0 min-h-[44px] rounded-xl border px-3 text-sm bg-white" />
        <button data-testid={`spotlight-save-${hub.id}`} disabled={!val.trim()} onClick={() => save(val)} className="press min-h-[44px] px-4 rounded-full bg-[#1E4D3B] text-white text-sm font-bold disabled:opacity-40">{a("Set", "Choisir")}</button>
        {current && <button data-testid={`spotlight-clear-${hub.id}`} onClick={() => save(null)} className="press min-h-[44px] px-3 rounded-full border text-sm font-bold">{a("Remove", "Retirer")}</button>}
      </div>
    </div>
  );
};

const Requests = ({ onApproved }) => {
  const { L, lang, hubOf } = useApp();
  const a = (en, fr) => (lang === "fr" ? fr : en);
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/spotlight-requests").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const decide = async (r, ok) => {
    try {
      await api.post(`/admin/spotlight-requests/${r.id}/${ok ? "approve" : "reject"}`);
      toast.success(ok ? a("Approved — now featured", "Approuvée — en vedette") : a("Request declined", "Demande refusée"));
      if (ok) onApproved(r.hub_id, { id: r.post.id, title: r.post.title, status: r.post.status });
      load();
    } catch { toast.error(a("This post is no longer live", "Cette publication n'est plus active")); load(); }
  };
  return (
    <div data-testid="spotlight-requests" className="mt-3 pt-3 border-t space-y-2">
      <h4 className="font-bold text-sm">{a("Requests from sellers", "Demandes des vendeurs")} ({items.length})</h4>
      {items.length === 0 && <p className="text-xs text-[#6B5E55]">{a("No pending requests.", "Aucune demande en attente.")}</p>}
      {items.map((r) => (
        <div key={r.id} data-testid={`sp-req-${r.id}`} className="rounded-xl bg-[#FAF7F2] p-2 flex gap-3 items-center">
          <a href={`/post/${r.post.id}`} target="_blank" rel="noreferrer" className="min-w-0 flex-1">
            <div className="font-bold text-sm truncate">{r.post.title}</div>
            <div className="text-xs text-[#6B5E55] truncate">{L(hubOf(r.hub_id))} · {r.post.poster?.name}{!r.live && ` · ${a("not live", "inactive")}`}</div>
          </a>
          <button data-testid={`sp-approve-${r.id}`} disabled={!r.live} onClick={() => decide(r, true)} className="press min-h-[44px] px-3 rounded-full bg-[#1E4D3B] text-white text-sm font-bold disabled:opacity-40">{a("Approve", "Approuver")}</button>
          <button data-testid={`sp-reject-${r.id}`} onClick={() => decide(r, false)} className="press min-h-[44px] px-3 rounded-full border text-sm font-bold">{a("Decline", "Refuser")}</button>
        </div>
      ))}
    </div>
  );
};

export const AdminSpotlight = () => {
  const { meta, lang } = useApp();
  const [cur, setCur] = useState(null);
  useEffect(() => { api.get("/admin/spotlight").then((r) => setCur(r.data)); }, []);
  if (!cur) return null;
  const hubs = meta.hubs.filter((h) => h.launch_status === "active");
  return (
    <div data-testid="admin-spotlight" className="rounded-2xl bg-[#FFFDF9] border p-4">
      <h3 className="font-extrabold inline-flex items-center gap-2"><Award className="w-5 h-5 text-[#D97706]" />{lang === "fr" ? "Coup de cœur sur l'accueil" : "Home page spotlight"}</h3>
      <p className="text-xs text-[#6B5E55] mb-1">{lang === "fr" ? "Une publication mise en avant par ville. Elle disparaît d'elle-même si elle est vendue ou expirée." : "One highlighted post per city. It hides itself automatically if sold or expired."}</p>
      <div className="divide-y">{hubs.map((h) => <HubRow key={h.id} hub={h} current={cur[h.id]} onSaved={(id, p) => setCur({ ...cur, [id]: p })} />)}</div>
      <Requests onApproved={(id, p) => setCur((c) => ({ ...c, [id]: p }))} />
    </div>
  );
};
